import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { getUserData } from "@/lib/api";
import { useTeamInvite } from "@/hooks/use-team-invite";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import { getLocalizedErrorMessage } from "@/lib/error-i18n";

type InviteStatus = "loading" | "valid" | "expired" | "invalid";

export default function Invite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { joinWithInvite, validateInvite, requestNewInvite } = useTeamInvite();
  const { toast } = useToast();
  const { t } = useTranslation();

  const [status, setStatus] = useState<InviteStatus>("loading");
  const [teamName, setTeamName] = useState<string | null>(null);
  const [isRequesting, setIsRequesting] = useState(false);

  const hasRun = useRef(false);

  useEffect(() => {
    const handleInvite = async () => {
      if (hasRun.current) return;
      hasRun.current = true;

      if (!token) {
        navigate("/teams");
        return;
      }

      // 🔹 1. VALIDATE TOKEN
      const validation = await validateInvite(token);

      if (!validation.success) {
        // save team name if backend provided it
        if (validation.data?.groupName) {
          setTeamName(validation.data.groupName);
        }

        if (validation.code === "INVITE_EXPIRED") {
          setStatus("expired");
        } else {
          setStatus("invalid");
        }
        return;
      }

      // ✅ VALID
      setTeamName(validation.data.groupName);
      setStatus("valid");

      const user = getUserData();

      // 🔐 NOT LOGGED IN
      if (!user) {
        localStorage.setItem("invite_token", token);
        navigate("/auth");
        return;
      }

      // 🔹 2. JOIN TEAM
      const res = await joinWithInvite(token);

      if (res.success) {
        localStorage.removeItem("invite_token");

        if (!res.alreadyMember) {
          toast({
            title: t("messages.inviteJoinedTitle"),
            description: t("messages.inviteJoinedDesc", { teamName: teamName || t("errors.thisTeam") }),
          });
        }

        navigate("/teams");
      } else {
        // update UI instead of redirect
        if (res.code === "INVITE_EXPIRED") {
          setStatus("expired");
          return;
        }

        if (res.code === "INVALID_INVITE") {
          setStatus("invalid");
          return;
        }

        toast({
          title: t("errors.inviteFailed"),
          description: getLocalizedErrorMessage(res.error, "errors.inviteFailed"),
          variant: "destructive",
        });

        navigate("/teams");
      }
    };

    handleInvite();
  }, [token, navigate, joinWithInvite, validateInvite, toast, teamName]);

  // 🔹 UI STATES

  // ⏳ Loading
  if (status === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // 🟠 Expired
  if (status === "expired") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 text-center">
        <h2 className="text-xl font-semibold">
          {t("errors.inviteExpiredTitle", { teamName: teamName || t("errors.thisTeam") })}
        </h2>

        <p className="text-muted-foreground">
          {t("errors.inviteExpiredDesc")}
        </p>

        <button
          className="px-4 py-2 rounded-lg border border-primary text-primary 
             hover:bg-primary hover:text-white transition 
             disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={isRequesting}
          onClick={async () => {
            if (!token || isRequesting) return;

            setIsRequesting(true);
            await requestNewInvite(token);
            setIsRequesting(false);
          }}
        >
          {isRequesting ? t("common.loading") : t("errors.requestNewInvite")}
        </button>

        <button
          className="px-4 py-2 rounded-lg border border-muted 
             hover:bg-muted transition"
          onClick={() => navigate("/")}
        >
          {t("common.goHome", { defaultValue: "Go Home" })}
        </button>
      </div>
    );
  }

  // 🔴 Invalid
  if (status === "invalid") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 text-center">
        <h2 className="text-xl font-semibold">
          {t("errors.invalidInviteTitle", { teamName: teamName || "" })}
        </h2>

        <p className="text-muted-foreground">
          {t("errors.invalidInviteDesc")}
        </p>

        <button
          className="btn btn-primary"
          onClick={async () => {
            if (!token) return;
            await requestNewInvite(token);
          }}
        >
          {t("errors.requestNewInvite")}
        </button>

        <button onClick={() => navigate("/")}>{t("common.goHome", { defaultValue: "Go Home" })}</button>
      </div>
    );
  }

  return null;
}
